"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/ChatOnboarding/Steps/DiagnosisUnlockFlow/DiagnosisUnlockFlowContent";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPlanReportDiagnosisUnlockChrome,
  mergePlanFetchChromePartial,
  resolveDiagnosisUnlockChrome,
} from "./diagnosisUnlockChrome";
import { DIAGNOSIS_UNLOCK_STEP1_BACKGROUND_SRC } from "./diagnosisUnlockModalShell";
import { DiagnosisUnlockDiagnosisStep } from "./DiagnosisStep/DiagnosisUnlockDiagnosisStep";
import { DiagnosisUnlockLoadingStep } from "./LoadingStep/DiagnosisUnlockLoadingStep";
import { DiagnosisUnlockUnlockStep } from "./UnlockStep/DiagnosisUnlockUnlockStep";
import {
  getDiagnosisUnlockFetchDismissAction,
  showDiagnosisUnlockFetchErrorToast,
} from "./diagnosisUnlockFetchError";
import {
  mapGoalDraftToPlanPayload,
  mapPayloadToDiagnosisView,
} from "./mapPresentRiskToDiagnosisUnlockView";
import {
  runStep2PrimaryCta,
  runStep3TerminalCta,
  runTopNavDismiss,
} from "./runDiagnosisUnlockCta";
import { useDiagnosisUnlockGoalPayload } from "./useDiagnosisUnlockGoalPayload";
import type {
  DiagnosisUnlockFlowResult,
  DiagnosisUnlockFlowStep,
  DiagnosisUnlockModalChromePartial,
  DiagnosisUnlockModalPayload,
} from "./types";

const I18n = I18nUtil.createScoped(Translations);

type DiagnosisUnlockFlowContentProps = {
  fetchGoalDetail: boolean;
  goalId?: string;
  payload?: DiagnosisUnlockModalPayload;
  initialStep: DiagnosisUnlockFlowStep;
  chrome?: DiagnosisUnlockModalChromePartial;
  step1BackgroundSrc?: string;
  hideActionSummarySection?: boolean;
  onFinish: (result: DiagnosisUnlockFlowResult) => void;
};

export const DiagnosisUnlockFlowContent: React.FC<
  DiagnosisUnlockFlowContentProps
> = ({
  fetchGoalDetail,
  goalId,
  payload: staticPayload,
  initialStep,
  chrome: chromePartial,
  step1BackgroundSrc,
  hideActionSummarySection = false,
  onFinish,
}) => {
  const [step, setStep] = useState<DiagnosisUnlockFlowStep>(initialStep);

  const fetchEnabled = fetchGoalDetail && step !== "step1";

  const {
    payload: resolvedPayload,
    isLoading: isGoalDetailLoading,
    isError: isGoalDetailError,
    hasEstablishedGoal,
  } = useDiagnosisUnlockGoalPayload({
    fetchGoalDetail,
    goalId,
    staticPayload,
    enabled: fetchEnabled,
  });

  const resolvedChrome = useMemo(() => {
    const mergedPartial =
      fetchGoalDetail && resolvedPayload
        ? mergePlanFetchChromePartial(
            chromePartial,
            getPlanReportDiagnosisUnlockChrome(hasEstablishedGoal),
          )
        : chromePartial;
    return resolveDiagnosisUnlockChrome(mergedPartial);
  }, [chromePartial, fetchGoalDetail, hasEstablishedGoal, resolvedPayload]);

  const diagnosisView = useMemo(
    () => (resolvedPayload ? mapPayloadToDiagnosisView(resolvedPayload) : null),
    [resolvedPayload],
  );

  const planPayload = useMemo(
    () =>
      resolvedPayload
        ? mapGoalDraftToPlanPayload(resolvedPayload.goalDraft)
        : null,
    [resolvedPayload],
  );

  const finish = useCallback(
    (action: string) => {
      onFinish({ action });
    },
    [onFinish],
  );

  useEffect(() => {
    if (!isGoalDetailError) {
      return;
    }
    showDiagnosisUnlockFetchErrorToast();
    finish(getDiagnosisUnlockFetchDismissAction());
  }, [finish, isGoalDetailError]);

  const handleStep1Complete = useCallback(() => {
    setStep("step2");
  }, []);

  const handleStep1Dismiss = useCallback(() => {
    const topNav = resolvedChrome.step1.topNav;
    if (topNav.variant === "close" && topNav.behavior === "dismiss") {
      void runTopNavDismiss(topNav, finish);
    }
  }, [finish, resolvedChrome.step1.topNav]);

  const handleStep2Primary = useCallback(() => {
    if (!resolvedPayload) {
      return;
    }
    void runStep2PrimaryCta(
      resolvedChrome.step2.primaryCta,
      () => {
        setStep("step3");
      },
      finish,
    );
  }, [finish, resolvedChrome.step2.primaryCta, resolvedPayload]);

  const handleStep2Dismiss = useCallback(() => {
    const topNav = resolvedChrome.step2.topNav;
    if (topNav.variant === "close" && topNav.behavior === "dismiss") {
      void runTopNavDismiss(topNav, finish);
    }
  }, [finish, resolvedChrome.step2.topNav]);

  const handleStep3TopNav = useCallback(() => {
    const topNav = resolvedChrome.step3.topNav;
    if (topNav.variant === "back" && topNav.behavior === "previous_step") {
      setStep("step2");
    } else if (topNav.variant === "close" && topNav.behavior === "dismiss") {
      void runTopNavDismiss(topNav, finish);
    }
  }, [finish, resolvedChrome.step3.topNav]);

  const handleStep3Primary = useCallback(() => {
    void runStep3TerminalCta(resolvedChrome.step3.primaryCta, finish);
  }, [finish, resolvedChrome.step3.primaryCta]);

  const handleStep3Secondary = useCallback(() => {
    const secondary = resolvedChrome.step3.secondaryCta;
    if (secondary?.visible) {
      void runStep3TerminalCta(secondary, finish);
    }
  }, [finish, resolvedChrome.step3.secondaryCta]);

  if (step === "step1") {
    return (
      <div
        className="h-full w-full overflow-hidden"
        data-testid="diagnosis-unlock-step1-shell"
      >
        <DiagnosisUnlockLoadingStep
          chrome={resolvedChrome.step1}
          backgroundSrc={
            step1BackgroundSrc ?? DIAGNOSIS_UNLOCK_STEP1_BACKGROUND_SRC
          }
          onComplete={handleStep1Complete}
          onDismiss={handleStep1Dismiss}
        />
      </div>
    );
  }

  if (step === "step2") {
    return (
      <DiagnosisUnlockDiagnosisStep
        chrome={resolvedChrome.step2}
        riskSummary={diagnosisView?.riskSummary ?? ""}
        findings={diagnosisView?.findings ?? []}
        isLoading={
          fetchGoalDetail &&
          !isGoalDetailError &&
          (isGoalDetailLoading || !resolvedPayload)
        }
        onPrimaryClick={handleStep2Primary}
        onDismiss={handleStep2Dismiss}
      />
    );
  }

  if (!planPayload) {
    return null;
  }

  return (
    <DiagnosisUnlockUnlockStep
      headline={I18n.t("Here's the plan I'd start with.")}
      plan={planPayload}
      chrome={resolvedChrome.step3}
      hideActionSummarySection={hideActionSummarySection}
      onPrimaryClick={handleStep3Primary}
      onSecondaryClick={handleStep3Secondary}
      onTopNavClick={handleStep3TopNav}
    />
  );
};
